<script lang="ts">
  import Icon from './Icon.svelte';
  import { BRAND_NAME } from '../lib/brand';
  import { onMount } from 'svelte';
  import { push, replace } from 'svelte-spa-router';
  import { session } from '../lib/session';
  import { api, ApiError } from '../lib/api';
  import { adminState } from '../lib/admin-state';
  import type { AdminCategoryDTO, AdminCategoryDetailDTO, ProductAccessScope } from '../lib/types';

  type LessonRow = AdminCategoryDetailDTO['materials'][number];

  // productId = null → create mode; otherwise edit an existing product.
  interface Props {
    productId?: string | null;
  }
  let { productId = null }: Props = $props();
  const isEdit = $derived(!!productId);

  let title = $state('');
  let description = $state('');
  let accessScope = $state<ProductAccessScope>('selected');
  let sendpulseVar = $state('');
  // Payment-name aliases, one per line in the textarea ↔ string[] on the wire.
  let paymentKeysText = $state('');
  let isSystem = $state(false);
  // Access is two-dimensional: whole categories vs individually-picked lessons.
  // A category is never in both (enforced by the toggles + the server).
  let selectedCategoryIds = $state<string[]>([]); // full-category grants
  let selectedMaterialIds = $state<string[]>([]); // per-lesson grants
  // Lessons per category, loaded once on mount so counts/checkboxes render
  // correctly even before a section is expanded.
  let lessonsByCat = $state<Record<string, LessonRow[]>>({});
  let expandedCats = $state<Set<string>>(new Set());

  // Tariffs: each has its own payment-name aliases + access period (days, or
  // null = lifetime). accessDays>0 = subscription of N days.
  interface TariffDraft {
    id?: string;
    title: string;
    paymentKeysText: string;
    accessDays: number | null;
    countsInRenewal: boolean;
  }
  let tariffs = $state<TariffDraft[]>([]);

  let categories = $state<AdminCategoryDTO[]>([]);
  let unmatched = $state<{ rawProduct: string; count: number }[]>([]);
  let loading = $state(true);
  let saving = $state(false);
  let deleting = $state(false);
  let error = $state<string | null>(null);

  onMount(async () => {
    if (!$session) {
      replace('/');
      return;
    }
    if (!$session.isAdmin) {
      replace('/home');
      return;
    }
    try {
      const [{ items }, product, unmatchedRes] = await Promise.all([
        api.adminCategories(),
        productId ? api.adminProduct(productId) : Promise.resolve(null),
        // In edit mode, also surface this product's own names not yet in a tariff.
        api.adminUnmatchedPayments(productId ?? undefined).catch(() => ({ items: [] })),
      ]);
      categories = items;
      unmatched = unmatchedRes.items;
      if (product) {
        title = product.title;
        description = product.description ?? '';
        accessScope = product.accessScope;
        sendpulseVar = product.sendpulseVar ?? '';
        paymentKeysText = product.paymentKeys.join('\n');
        isSystem = product.isSystem;
        selectedCategoryIds = [...product.categoryIds];
        selectedMaterialIds = [...product.materialIds];
        tariffs = product.tariffs.map((t) => ({
          id: t.id,
          title: t.title,
          paymentKeysText: t.paymentKeys.join('\n'),
          accessDays: t.accessDays,
          countsInRenewal: t.countsInRenewal,
        }));
      }
      // Load every category's lessons in parallel so per-lesson selection and
      // counts are available immediately. Small admin dataset — a burst of
      // cheap GETs is fine and keeps the UI logic synchronous.
      const details = await Promise.all(
        items.map((c) => api.adminCategory(c.id).catch(() => null)),
      );
      const map: Record<string, LessonRow[]> = {};
      for (const d of details) if (d) map[d.id] = d.materials;
      lessonsByCat = map;
      // Auto-expand sections that already have individually-picked lessons so
      // the admin sees the current selection without hunting for it.
      if (selectedMaterialIds.length > 0) {
        const sel = new Set(selectedMaterialIds);
        const toExpand = new Set<string>();
        for (const [catId, lessons] of Object.entries(map)) {
          if (lessons.some((l) => sel.has(l.id))) toExpand.add(catId);
        }
        expandedCats = toExpand;
      }
    } catch (err) {
      error = err instanceof ApiError ? `Не вдалось завантажити (${err.code})` : 'Не вдалось завантажити';
    } finally {
      loading = false;
    }
  });

  function toggleExpand(id: string): void {
    const next = new Set(expandedCats);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    expandedCats = next;
  }

  const isWholeCategory = (id: string): boolean => selectedCategoryIds.includes(id);

  /** Toggle «весь розділ». Turning it on clears any individually-picked lessons
   *  of that category (invariant: never whole + part). */
  function toggleWholeCategory(id: string): void {
    if (isWholeCategory(id)) {
      selectedCategoryIds = selectedCategoryIds.filter((x) => x !== id);
    } else {
      selectedCategoryIds = [...selectedCategoryIds, id];
      const lessonIds = new Set((lessonsByCat[id] ?? []).map((l) => l.id));
      selectedMaterialIds = selectedMaterialIds.filter((mid) => !lessonIds.has(mid));
    }
  }

  /** Toggle a single lesson. Picking a lesson drops the «весь розділ» grant for
   *  its category so the two modes never coexist. */
  function toggleMaterial(catId: string, matId: string): void {
    if (selectedMaterialIds.includes(matId)) {
      selectedMaterialIds = selectedMaterialIds.filter((x) => x !== matId);
    } else {
      selectedMaterialIds = [...selectedMaterialIds, matId];
      if (isWholeCategory(catId)) {
        selectedCategoryIds = selectedCategoryIds.filter((x) => x !== catId);
      }
    }
  }

  /** How many lessons of a category are individually picked (0 when whole). */
  function catPickedCount(catId: string): number {
    const lessons = lessonsByCat[catId] ?? [];
    const sel = new Set(selectedMaterialIds);
    return lessons.reduce((n, l) => n + (sel.has(l.id) ? 1 : 0), 0);
  }

  /** Short badge describing a category's grant state for the collapsed row. */
  function catBadge(catId: string): string {
    if (isWholeCategory(catId)) return 'Весь розділ';
    const n = catPickedCount(catId);
    return n > 0 ? `${n} ${n === 1 ? 'урок' : 'урок(и)'}` : '';
  }

  const splitKeys = (text: string): string[] =>
    text.split('\n').map((s) => s.trim()).filter(Boolean);
  const norm = (s: string): string => s.trim().toLowerCase();

  // ── Tariff editor modal ──
  let tariffModalOpen = $state(false);
  let editingIndex = $state<number | null>(null); // null = new tariff
  let dId = $state<string | undefined>(undefined);
  let dTitle = $state('');
  let dPaymentKeysText = $state('');
  let dAccessDays = $state<number | null>(30);
  let dCountsInRenewal = $state(true);
  let deleteIndex = $state<number | null>(null); // tariff pending delete confirmation

  function openNewTariff(): void {
    editingIndex = null;
    dId = undefined;
    dTitle = '';
    dPaymentKeysText = '';
    dAccessDays = 30;
    dCountsInRenewal = true;
    tariffModalOpen = true;
  }
  function openEditTariff(i: number): void {
    const t = tariffs[i];
    editingIndex = i;
    dId = t.id;
    dTitle = t.title;
    dPaymentKeysText = t.paymentKeysText;
    dAccessDays = t.accessDays;
    dCountsInRenewal = t.countsInRenewal;
    tariffModalOpen = true;
  }
  function closeTariffModal(): void {
    tariffModalOpen = false;
  }
  // accessDays: >0 = subscription N days, 0 = no access (reservation), null = lifetime.
  const draftValid = $derived(dTitle.trim().length > 0 && (dAccessDays === null || dAccessDays >= 0));
  function saveTariffModal(): void {
    if (!draftValid) return;
    const draft = {
      id: dId,
      title: dTitle.trim(),
      paymentKeysText: dPaymentKeysText,
      accessDays: dAccessDays,
      countsInRenewal: dCountsInRenewal,
    };
    if (editingIndex === null) tariffs = [...tariffs, draft];
    else tariffs = tariffs.map((t, idx) => (idx === editingIndex ? draft : t));
    tariffModalOpen = false;
  }

  function confirmDeleteTariff(): void {
    if (deleteIndex === null) return;
    tariffs = tariffs.filter((_, idx) => idx !== deleteIndex);
    deleteIndex = null;
  }

  // Human summary of a tariff's access period for the list card.
  function periodLabel(accessDays: number | null): string {
    if (accessDays === null) return 'Безстроковий';
    if (accessDays === 0) return 'Без доступу';
    return `Підписка · ${accessDays} дн`;
  }

  // Suggestions inside the modal: unmatched names not claimed by OTHER tariffs
  // (or the product-level fallback) and not already in the draft.
  const draftKeys = $derived(new Set(splitKeys(dPaymentKeysText).map(norm)));
  // Exclude only names already used by OTHER tariffs — the product-level fallback
  // names ARE offered here (that's how the admin moves them into tariffs).
  const otherClaimed = $derived(
    new Set(
      tariffs
        .filter((_, idx) => idx !== editingIndex)
        .flatMap((t) => splitKeys(t.paymentKeysText))
        .map(norm),
    ),
  );
  const modalSuggestions = $derived(
    unmatched.filter((u) => {
      const n = norm(u.rawProduct);
      return !otherClaimed.has(n) && !draftKeys.has(n);
    }),
  );
  function addKeyToDraft(name: string): void {
    const t = name.trim();
    if (!t || draftKeys.has(norm(t))) return;
    dPaymentKeysText = dPaymentKeysText.trim() ? `${dPaymentKeysText.trim()}\n${t}` : t;
  }

  // A selected product needs an ownership source (SendPulse variable OR ≥1
  // tariff for purchase-driven access) plus some content (a category or lesson).
  const hasOwnershipSource = $derived(sendpulseVar.trim().length > 0 || tariffs.length >= 1);
  const canSave = $derived(
    title.trim().length > 0 &&
      (accessScope === 'all' ||
        (hasOwnershipSource &&
          (selectedCategoryIds.length > 0 || selectedMaterialIds.length > 0))),
  );

  async function save(): Promise<void> {
    if (!canSave || saving) return;
    saving = true;
    error = null;
    const body = {
      title: title.trim(),
      description: description.trim() || null,
      accessScope,
      sendpulseVar: accessScope === 'selected' ? sendpulseVar.trim() : null,
      paymentKeys: splitKeys(paymentKeysText),
      categoryIds: accessScope === 'selected' ? selectedCategoryIds : [],
      materialIds: accessScope === 'selected' ? selectedMaterialIds : [],
      tariffs: tariffs
        .map((t) => ({
          id: t.id,
          title: t.title.trim(),
          paymentKeys: splitKeys(t.paymentKeysText),
          accessDays: t.accessDays,
          countsInRenewal: t.countsInRenewal,
        }))
        .filter((t) => t.title.length > 0),
    };
    try {
      if (productId) await api.adminPatchProduct(productId, body);
      else await api.adminCreateProduct(body);
      adminState.activeTab = 'products';
      push('/admin/dashboard');
    } catch (err) {
      error = err instanceof ApiError ? `Не вдалось зберегти (${err.code})` : 'Не вдалось зберегти';
    } finally {
      saving = false;
    }
  }

  async function remove(): Promise<void> {
    if (!productId || isSystem || deleting) return;
    if (!confirm('Видалити продукт? Доступи, що він відкривав, зникнуть у користувачів.')) return;
    deleting = true;
    error = null;
    try {
      await api.adminDeleteProduct(productId);
      adminState.activeTab = 'products';
      push('/admin/dashboard');
    } catch (err) {
      error = err instanceof ApiError ? `Не вдалось видалити (${err.code})` : 'Не вдалось видалити';
      deleting = false;
    }
  }
</script>

<div class="screen">
  <div class="admin-header">
    <a href="#/admin/dashboard" class="back-link">← Назад</a>
    <div class="header-title">{isEdit ? 'Редагувати продукт' : 'Новий продукт'}</div>
    <span class="header-spacer"></span>
  </div>

  <div class="scroll">
    {#if loading}
      <div class="placeholder">Завантажуємо…</div>
    {:else}
      <label class="field-label">Назва</label>
      <input class="field" maxlength="120" placeholder="Напр. Міні-курс: Легкі ноги" bind:value={title} />

      <label class="field-label">Опис (необовʼязково)</label>
      <textarea class="field" maxlength="2000" placeholder="Короткий опис продукту…" bind:value={description}></textarea>

      <label class="field-label">Тип доступу</label>
      <div class="status-pills">
        <button
          type="button"
          class="status-pill"
          class:active={accessScope === 'selected'}
          disabled={isSystem}
          onclick={() => (accessScope = 'selected')}
        >Міні-курс</button>
        <button
          type="button"
          class="status-pill"
          class:active={accessScope === 'all'}
          disabled={isSystem}
          onclick={() => (accessScope = 'all')}
        >Простір (повний доступ)</button>
      </div>
      {#if isSystem}
        <div class="hint">Системний продукт — тип доступу зафіксовано, видалити не можна.</div>
      {/if}

      {#if accessScope === 'selected'}
        <label class="field-label">Змінна SendPulse (необовʼязково)</label>
        <input
          class="field"
          maxlength="200"
          placeholder="subscription_end_date_легкі_ноги"
          bind:value={sendpulseVar}
        />
        <div class="hint">
          Назва змінної контакту в SendPulse з датою закінчення доступу.
          {#if sendpulseVar.trim().length === 0}
            <br /><strong>Порожньо</strong> — доступ дається <strong>покупкою тарифу</strong>
            (нижче). Безстроковий тариф = безмежний доступ.
          {/if}
        </div>

        <label class="field-label">Доступ до груп та уроків</label>
        <div class="hint" style="margin-bottom:8px;">
          Постав галочку «Весь розділ», щоб відкрити всі уроки, або розгорни розділ
          і обери окремі уроки.
        </div>
        {#if categories.length === 0}
          <div class="placeholder">Немає розділів</div>
        {:else}
          <div class="cat-list">
            {#each categories as c (c.id)}
              {@const lessons = lessonsByCat[c.id] ?? []}
              {@const whole = isWholeCategory(c.id)}
              {@const badge = catBadge(c.id)}
              <div class="cat-block" class:active={whole || catPickedCount(c.id) > 0}>
                <div class="cat-head">
                  <!-- svelte-ignore a11y_label_has_associated_control -->
                  <label class="cat-whole">
                    <input
                      type="checkbox"
                      checked={whole}
                      onchange={() => toggleWholeCategory(c.id)}
                    />
                    <span class="cat-emoji"><Icon name={c.icon} /></span>
                    <span class="cat-name">{c.name}</span>
                  </label>
                  {#if !c.isActive}<span class="cat-draft">чернетка</span>{/if}
                  {#if badge}<span class="cat-badge">{badge}</span>{/if}
                  <button
                    type="button"
                    class="cat-expand"
                    class:open={expandedCats.has(c.id)}
                    onclick={() => toggleExpand(c.id)}
                    aria-label="Показати уроки"
                    disabled={lessons.length === 0}
                  >▾</button>
                </div>

                {#if expandedCats.has(c.id)}
                  <div class="lesson-list">
                    {#if lessons.length === 0}
                      <div class="lesson-empty">У розділі немає уроків</div>
                    {:else}
                      {#each lessons as l (l.id)}
                        <!-- svelte-ignore a11y_label_has_associated_control -->
                        <label class="lesson-row" class:checked={whole || selectedMaterialIds.includes(l.id)}>
                          <input
                            type="checkbox"
                            checked={whole || selectedMaterialIds.includes(l.id)}
                            disabled={whole}
                            onchange={() => toggleMaterial(c.id, l.id)}
                          />
                          <span class="lesson-title">{l.title}</span>
                          {#if !l.isPublished}<span class="cat-draft">чернетка</span>{/if}
                        </label>
                      {/each}
                    {/if}
                    {#if whole}
                      <div class="lesson-note">Відкрито весь розділ — усі уроки включені.</div>
                    {/if}
                  </div>
                {/if}
              </div>
            {/each}
          </div>
        {/if}
      {:else}
        <div class="all-note">Простір відкриває всі групи матеріалів автоматично.</div>
      {/if}

      <!-- Тарифи -->
      <div class="tariffs-head">
        <label class="field-label" style="margin:0;">Тарифи</label>
        <button type="button" class="tariff-add" onclick={openNewTariff}>+ Тариф</button>
      </div>
      <div class="hint" style="margin-bottom:8px;">
        Кожен тариф має свої назви оплат і період доступу (у днях). За цим періодом
        дашборд рахує активних/закінчених учасниць.
      </div>

      {#if tariffs.length === 0}
        <div class="tariffs-empty">Тарифів ще немає. Натисни «+ Тариф».</div>
      {:else}
        {#each tariffs as t, i (t.id ?? `new-${i}`)}
          <div class="tariff-card">
            <div class="tariff-card-main">
              <div class="tariff-card-title">{t.title || 'Без назви'}</div>
              <div class="tariff-card-meta">
                {periodLabel(t.accessDays)} · {splitKeys(t.paymentKeysText).length} назв оплат{#if t.accessDays !== null && t.accessDays > 0 && !t.countsInRenewal} · поза статистикою продовжень{/if}
              </div>
            </div>
            <button type="button" class="tariff-icon" onclick={() => openEditTariff(i)} aria-label="Редагувати"><Icon name="square-pen" /></button>
            <button type="button" class="tariff-icon del" onclick={() => (deleteIndex = i)} aria-label="Видалити"><Icon name="trash-2" /></button>
          </div>
        {/each}
      {/if}

      {#if error}<div class="error">{error}</div>{/if}

      <button type="button" class="submit" disabled={!canSave || saving} onclick={save}>
        {saving ? 'Зберігаємо…' : isEdit ? 'Зберегти зміни' : 'Створити продукт'}
      </button>

      {#if isEdit && !isSystem}
        <button type="button" class="delete" disabled={deleting} onclick={remove}>
          {deleting ? 'Видаляємо…' : 'Видалити продукт'}
        </button>
      {/if}
    {/if}
  </div>

  <!-- Tariff editor modal -->
  {#if tariffModalOpen}
    <!-- svelte-ignore a11y_click_events_have_key_events a11y_no_static_element_interactions -->
    <div class="modal-backdrop" onclick={closeTariffModal}></div>
    <div class="modal-sheet" role="dialog" aria-modal="true" aria-label="Тариф">
      <div class="modal-head">
        <div class="modal-title">{editingIndex === null ? 'Новий тариф' : 'Редагувати тариф'}</div>
        <button type="button" class="modal-close" onclick={closeTariffModal} aria-label="Закрити"><Icon name="x" /></button>
      </div>
      <div class="modal-body">
        <label class="field-label">Назва тарифу</label>
        <input class="field" placeholder="напр. 1 місяць" bind:value={dTitle} />

        <label class="field-label">Період доступу</label>
        <div class="tariff-period">
          <button
            type="button"
            class="period-pill"
            class:on={dAccessDays !== null && dAccessDays > 0}
            onclick={() => (dAccessDays = dAccessDays && dAccessDays > 0 ? dAccessDays : 30)}
          >Підписка</button>
          <button
            type="button"
            class="period-pill"
            class:on={dAccessDays === null}
            onclick={() => (dAccessDays = null)}
          >Безстроковий</button>
          <button
            type="button"
            class="period-pill"
            class:on={dAccessDays === 0}
            onclick={() => (dAccessDays = 0)}
          >Без доступу</button>
          {#if dAccessDays !== null && dAccessDays > 0}
            <input class="field period-days" type="number" min="1" bind:value={dAccessDays} />
            <span class="period-unit">днів доступу</span>
          {/if}
        </div>
        {#if dAccessDays === 0}
          <div class="hint" style="margin-top:0;">Оплата враховується у виручці, але не дає активного доступу (бронь/завдаток).</div>
        {/if}

        {#if dAccessDays !== null && dAccessDays > 0}
          <label class="renewal-check">
            <input type="checkbox" bind:checked={dCountsInRenewal} />
            <span>
              Враховувати в статистиці «Продовження / Підписка закінчилася»
              <span class="renewal-hint">
                {#if dCountsInRenewal}
                  Закінчення цього тарифу рахується як точка продовження.
                {:else}
                  Закінчення цього тарифу не рахується ні продовженням, ні втратою — доречно для
                  пробного доступу, де кінець періоду це конверсія в платний, а не відтік. На доступ
                  не впливає: власники тарифу лишаються «Активними» весь свій період.
                {/if}
              </span>
            </span>
          </label>
        {/if}

        <label class="field-label">Назви оплат</label>
        <textarea
          class="field"
          rows="3"
          placeholder={`${BRAND_NAME} (1 місяць)`}
          bind:value={dPaymentKeysText}
        ></textarea>
        {#if modalSuggestions.length > 0}
          <div class="suggest">
            <div class="suggest-title">Незіставлені оплати — натисни, щоб додати:</div>
            <div class="suggest-chips">
              {#each modalSuggestions as u (u.rawProduct)}
                <button type="button" class="suggest-chip" onclick={() => addKeyToDraft(u.rawProduct)}>
                  + {u.rawProduct} <span class="suggest-count">{u.count}</span>
                </button>
              {/each}
            </div>
          </div>
        {/if}

        <button type="button" class="submit" disabled={!draftValid} onclick={saveTariffModal}>
          Зберегти
        </button>
      </div>
    </div>
  {/if}

  <!-- Delete-tariff confirmation -->
  {#if deleteIndex !== null}
    <!-- svelte-ignore a11y_click_events_have_key_events a11y_no_static_element_interactions -->
    <div class="modal-backdrop" onclick={() => (deleteIndex = null)}></div>
    <div class="modal-sheet modal-confirm" role="dialog" aria-modal="true" aria-label="Видалити тариф">
      <div class="modal-head">
        <div class="modal-title">Видалити тариф «{tariffs[deleteIndex]?.title || 'Без назви'}»?</div>
      </div>
      <div class="modal-body">
        <div class="confirm-text">
          Усі назви оплат, прив'язані до цього тарифу, стануть вільними — їх можна буде
          додати в інші тарифи. Оплати не видаляються.
        </div>
        <div class="confirm-actions">
          <button type="button" class="confirm-cancel" onclick={() => (deleteIndex = null)}>Скасувати</button>
          <button type="button" class="confirm-delete" onclick={confirmDeleteTariff}>Видалити</button>
        </div>
      </div>
    </div>
  {/if}
</div>

<style>
  .screen { display: flex; flex-direction: column; flex: 1; min-height: 0; background: var(--bg); }
  .admin-header {
    background: linear-gradient(135deg, var(--dark), var(--dark3));
    padding: 14px 18px;
    color: var(--text-light);
    display: flex;
    align-items: center;
    gap: 10px;
    flex-shrink: 0;
  }
  .back-link { font-size: 12px; color: rgba(251, 246, 236, 0.9); text-decoration: none; }
  .header-title { flex: 1; text-align: center; font-size: 14px; font-weight: 600; }
  .header-spacer { width: 40px; }

  .scroll { flex: 1; overflow-y: auto; padding: 18px 18px 100px; }
  .placeholder {
    padding: 16px; text-align: center; color: var(--text-muted); font-size: 13px;
    background: var(--bg2); border: 1px dashed var(--border2); border-radius: 10px;
  }
  .field-label {
    display: block; font-size: 11px; font-weight: 600; color: var(--text-muted);
    margin: 14px 0 6px; letter-spacing: 0.5px; text-transform: uppercase;
  }
  .field {
    width: 100%; border: 1px solid var(--border2); border-radius: 10px; padding: 11px 13px;
    font-size: 13px; color: var(--text); background: var(--bg); font-family: inherit;
    outline: none; margin-bottom: 4px;
  }
  .field:focus { border-color: var(--outline); }
  textarea.field { resize: vertical; min-height: 80px; }
  .hint { font-size: 11px; color: var(--text-muted); margin: 4px 2px 0; line-height: 1.4; }

  /* Галочка «враховувати в статистиці продовжень» + пояснення під нею. */
  .renewal-check {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    margin: 14px 2px 0;
    font-size: 12px;
    color: var(--text);
    line-height: 1.4;
    cursor: pointer;
  }
  .renewal-check input { margin-top: 2px; flex-shrink: 0; accent-color: var(--pu); }
  .renewal-hint { display: block; font-size: 11px; color: var(--text-muted); margin-top: 3px; }

  /* Tariffs */
  .tariffs-head { display: flex; align-items: center; justify-content: space-between; margin: 16px 0 0; }
  .tariff-add {
    border: 1px solid var(--pu2); background: var(--bg); color: var(--accent-text);
    padding: 5px 12px; border-radius: 8px; font-size: 12px; font-weight: 600;
    cursor: pointer; font-family: inherit;
  }
  .tariffs-empty {
    padding: 14px; text-align: center; color: var(--text-muted); font-size: 12px;
    background: var(--bg2); border: 1px dashed var(--border2); border-radius: 10px; margin-bottom: 10px;
  }
  .tariff-card {
    display: flex; align-items: center; gap: 10px;
    border: 1px solid var(--border); background: var(--bg2); border-radius: 12px;
    padding: 11px 12px; margin-bottom: 8px;
  }
  .tariff-card-main { flex: 1; min-width: 0; }
  .tariff-card-title { font-size: 13px; font-weight: 600; color: var(--text); }
  .tariff-card-meta { font-size: 11px; color: var(--text-muted); margin-top: 3px; }
  .tariff-icon {
    flex-shrink: 0; width: 32px; height: 32px; border: 1px solid var(--border2);
    background: var(--bg); color: var(--text-muted); border-radius: 8px; cursor: pointer;
    font-size: 14px;
  }
  .tariff-icon.del { color: var(--status-inactive); }
  .tariff-period { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; margin: 4px 0 0; }
  .period-pill {
    border: 1px solid var(--border2); background: var(--bg); color: var(--text-muted);
    padding: 5px 12px; border-radius: 999px; font-size: 12px; font-weight: 600;
    cursor: pointer; font-family: inherit;
  }
  .period-pill.on { background: var(--pu); color: var(--text-light); border-color: var(--pu); }
  .period-days { width: 78px; margin-bottom: 0; padding: 7px 10px; text-align: center; }
  .period-unit { font-size: 12px; color: var(--text-muted); }

  .suggest {
    margin-top: 10px;
    padding: 10px 12px;
    background: var(--pu-light);
    border: 1px solid var(--border);
    border-radius: 10px;
  }
  .suggest-title {
    font-size: 11px;
    font-weight: 600;
    color: var(--pu3, var(--pu));
    margin-bottom: 8px;
  }
  .suggest-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  }
  .suggest-chip {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    border: 1px solid var(--pu2);
    background: var(--bg);
    color: var(--accent-text);
    padding: 5px 10px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    font-family: inherit;
  }
  .suggest-chip:active { background: var(--pu-light); }
  .suggest-count {
    font-size: 10px;
    color: var(--text-muted);
    background: var(--bg2);
    border-radius: 999px;
    padding: 1px 6px;
  }

  .status-pills { display: flex; gap: 6px; margin: 4px 0 8px; }
  .status-pill {
    flex: 1; border: 1px solid var(--border2); background: var(--bg); color: var(--text-muted);
    padding: 9px 12px; border-radius: 10px; font-size: 12px; font-weight: 600; cursor: pointer;
    font-family: inherit; transition: background 0.15s, color 0.15s, border-color 0.15s;
  }
  .status-pill.active { background: var(--pu); color: var(--text-light); border-color: var(--pu); }
  .status-pill:disabled { opacity: 0.5; cursor: not-allowed; }

  .cat-list { display: flex; flex-direction: column; gap: 6px; }
  .cat-block {
    background: var(--bg2); border: 1px solid var(--border); border-radius: 10px;
    overflow: hidden;
  }
  .cat-block.active { border-color: var(--outline); background: var(--pu-light); }
  .cat-head { display: flex; align-items: center; gap: 10px; padding: 10px 12px; }
  .cat-whole {
    display: flex; align-items: center; gap: 10px; flex: 1; min-width: 0; cursor: pointer;
  }
  .cat-head input { width: 17px; height: 17px; accent-color: var(--pu); flex-shrink: 0; }
  .cat-emoji { font-size: 18px; }
  .cat-name {
    flex: 1; min-width: 0; font-size: 13px; font-weight: 600; color: var(--text);
    overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  }
  .cat-draft {
    font-size: 10px; font-weight: 600; padding: 2px 7px; border-radius: 6px;
    background: var(--bg); color: var(--text-muted); flex-shrink: 0;
  }
  .cat-badge {
    font-size: 10px; font-weight: 700; padding: 2px 8px; border-radius: 6px;
    background: var(--pu); color: var(--text-light); flex-shrink: 0; white-space: nowrap;
  }
  .cat-expand {
    flex-shrink: 0; width: 28px; height: 28px; border: 1px solid var(--border2);
    background: var(--bg); color: var(--text-muted); border-radius: 8px; cursor: pointer;
    font-size: 12px; transition: transform 0.15s;
  }
  .cat-expand.open { transform: rotate(180deg); }
  .cat-expand:disabled { opacity: 0.4; cursor: not-allowed; }
  .lesson-list {
    display: flex; flex-direction: column; gap: 4px;
    padding: 4px 12px 10px 12px; border-top: 1px solid var(--border);
  }
  .lesson-row {
    display: flex; align-items: center; gap: 9px; padding: 7px 10px;
    background: var(--bg); border: 1px solid var(--border); border-radius: 8px; cursor: pointer;
  }
  .lesson-row.checked { border-color: var(--outline); }
  .lesson-row input { width: 15px; height: 15px; accent-color: var(--pu); flex-shrink: 0; }
  .lesson-row input:disabled { opacity: 0.6; }
  .lesson-title {
    flex: 1; min-width: 0; font-size: 12px; color: var(--text);
    overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  }
  .lesson-empty, .lesson-note {
    font-size: 11px; color: var(--text-muted); padding: 6px 4px;
  }
  .all-note {
    padding: 12px 14px; background: var(--bg2); border: 1px solid var(--border);
    border-radius: 10px; font-size: 12px; color: var(--text-muted); line-height: 1.5;
  }

  .error {
    margin: 12px 0; padding: 10px 14px; background: var(--danger-bg); color: var(--status-inactive);
    border: 1px solid var(--danger-border); border-radius: 10px; font-size: 12px;
  }
  .submit {
    width: 100%; margin-top: 18px; padding: 14px; border: none; border-radius: 12px;
    background: linear-gradient(135deg, var(--pu), var(--pu3)); color: var(--text-light);
    font-size: 14px; font-weight: 600; cursor: pointer; font-family: inherit; transition: opacity 0.15s;
  }
  .submit:disabled { opacity: 0.5; cursor: not-allowed; }
  .delete {
    width: 100%; margin-top: 10px; padding: 13px; border: 1px solid var(--danger-border); border-radius: 12px;
    background: var(--bg); color: var(--status-inactive); font-size: 13px; font-weight: 600; cursor: pointer;
    font-family: inherit;
  }
  .delete:disabled { opacity: 0.5; cursor: not-allowed; }

  /* Modals */
  .modal-backdrop {
    position: fixed; inset: 0; background: rgba(0, 0, 0, 0.5); z-index: 50;
  }
  .modal-sheet {
    position: fixed; left: 0; right: 0; bottom: 0; z-index: 51;
    background: var(--bg); border-radius: 18px 18px 0 0; max-height: 88vh;
    display: flex; flex-direction: column;
    max-width: 640px; margin: 0 auto;
  }
  .modal-confirm { border-radius: 16px; bottom: auto; top: 50%; transform: translateY(-50%); margin: 0 16px; }
  .modal-head {
    display: flex; align-items: center; gap: 10px; padding: 14px 16px;
    border-bottom: 1px solid var(--border); flex-shrink: 0;
  }
  .modal-title { flex: 1; font-size: 14px; font-weight: 700; color: var(--text); }
  .modal-close {
    flex-shrink: 0; width: 30px; height: 30px; border: none; background: var(--bg2);
    color: var(--text-muted); border-radius: 999px; cursor: pointer; font-size: 13px;
  }
  .modal-body { padding: 14px 16px 20px; overflow-y: auto; }
  .confirm-text { font-size: 13px; color: var(--text); line-height: 1.5; margin-bottom: 16px; }
  .confirm-actions { display: flex; gap: 10px; }
  .confirm-cancel {
    flex: 1; padding: 11px; border: 1px solid var(--border2); border-radius: 10px;
    background: var(--bg); color: var(--text); font-size: 13px; font-weight: 600;
    cursor: pointer; font-family: inherit;
  }
  .confirm-delete {
    flex: 1; padding: 11px; border: 1px solid var(--danger-border); border-radius: 10px;
    background: var(--bg); color: var(--status-inactive); font-size: 13px; font-weight: 600;
    cursor: pointer; font-family: inherit;
  }
</style>
